import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle, ChevronRight, Layers } from 'lucide-react';
import { services, myriadeColors, getServiceGradient, getServiceLightColor } from "./data";

const ServicesDisplay = () => {
    const [activeService, setActiveService] = useState(0);

    return (
        <section className="relative py-25">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-black" />

            {/* Subtle radial gradient */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(120,119,198,0.1),transparent_50%)]" />

            {/* Animated particles */}
            <div className="absolute inset-0 overflow-hidden">
                {[...Array(15)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-cyan-400/20 rounded-full"
                        animate={{
                            x: [0, Math.random() * 100 - 50],
                            y: [0, Math.random() * 100 - 50],
                        }}
                        transition={{
                            duration: 5 + Math.random() * 3,
                            repeat: Infinity,
                            delay: i * 0.3
                        }}
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="text-center mb-20"
                >
                    {/* <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full mb-6 border border-blue-500/20">
                        <Sparkles className="w-5 h-5 text-blue-400" />
                        <span className="text-blue-400 font-medium">Solutions Premium</span>
                    </div> */}

                    <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6">
                        <span className="block">Nos Services</span>
                        <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-600 bg-clip-text text-transparent px-2 sm:px-4">
                            d'Excellence
                        </span>
                    </h2>

                    <p className="text-base sm:text-xl text-gray-300 max-w-2xl mx-auto px-4">
                        Nous combinons expertise technique et vision stratégique pour développer des solutions digitales qui propulsent votre entreprise.
                    </p>
                </motion.div>

                {/* Services Navigation */}
                <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-12">
                    {services.map((service, index) => (
                        <motion.button
                            key={index}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setActiveService(index)}
                            style={activeService === index ? {
                                background: getServiceGradient(service),
                                color: 'white'
                            } : {
                                background: 'rgba(255, 255, 255, 0.05)',
                                color: '#e5e7eb',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                            className="group relative px-4 py-2 sm:px-6 sm:py-3 cursor-pointer rounded-xl font-medium transition-all shadow-lg hover:shadow-xl backdrop-blur-sm text-sm sm:text-base"
                        >
                            <span className="flex items-center gap-2">
                                {React.createElement(service.icon, {
                                    className: "w-4 h-4 sm:w-5 sm:h-5",
                                    style: { color: activeService === index ? 'white' : service.colorValue }
                                })}
                                {service.title.split(' ')[0]}
                            </span>

                            {activeService === index && (
                                <motion.div
                                    layoutId="activeTab"
                                    className="absolute inset-0 rounded-xl -z-10"
                                    style={{ background: getServiceLightColor(service) }}
                                />
                            )}
                        </motion.button>
                    ))}
                </div>

                {/* Main Services Display */}
                <div className="grid lg:grid-cols-12 gap-8">
                    {/* Left Column - Service Details */}
                    <motion.div
                        key={activeService}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="lg:col-span-7"
                    >
                        <div className="relative group">
                            {/* Floating Background */}
                            {/* <motion.div
                                animate={{
                                    rotate: 360,
                                    scale: [1, 1.02, 1]
                                }}
                                transition={{
                                    rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                                    scale: { duration: 4, repeat: Infinity }
                                }}
                                className="absolute -inset-4 rounded-3xl opacity-20"
                                style={{ background: getServiceGradient(services[activeService]) }}
                            /> */}

                            <div className="relative bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-gray-800 p-6 md:p-8 shadow-xl">
                                {/* Service Header */}
                                <div className="flex flex-col md:flex-row items-center md:items-start justify-between mb-6 md:mb-8">
                                    <div className="w-full">
                                        <div className="flex flex-col md:flex-row items-center md:items-start gap-4 mb-4">
                                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0"
                                                style={{ background: getServiceGradient(services[activeService]) }}>
                                                {React.createElement(services[activeService].icon, { className: "w-8 h-8 text-white" })}
                                            </div>

                                            <div className="w-full text-center md:text-left">
                                                <h3 className="text-xl md:text-3xl font-bold text-white mb-2">
                                                    {services[activeService].title}
                                                </h3>
                                                <p className="font-medium text-sm md:text-base" style={{ color: services[activeService].colorValue }}>
                                                    {services[activeService].tagline}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Service Description */}
                                <div className="mb-8">
                                    <p className="text-base md:text-lg text-gray-300 mb-6 text-center md:text-left">
                                        {services[activeService].description}
                                    </p>

                                    {/* Features */}
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {[
                                            "Solution sur-mesure adaptée",
                                            "Support technique dédié",
                                            "Délais de livraison optimisés",
                                            "Maintenance proactive"
                                        ].map((feature, index) => (
                                            <div key={index} className="flex items-center gap-3 p-4 rounded-xl border"
                                                style={{
                                                    background: getServiceLightColor(services[activeService]),
                                                    borderColor: `${services[activeService].colorValue}20`
                                                }}>
                                                <CheckCircle className="w-5 h-5 shrink-0"
                                                    style={{ color: services[activeService].colorValue }} />
                                                <span className="text-gray-200">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Service Image */}
                                <motion.div
                                    animate={{
                                        scale: [1, 1.02, 1],
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity
                                    }}
                                    className="relative rounded-xl overflow-hidden border border-gray-700"
                                >
                                    <img
                                        src={services[activeService].image}
                                        alt={services[activeService].title}
                                        className="w-full h-48 md:h-64 object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right Column - All Services Preview */}
                    <div className="lg:col-span-5">
                        <div className="sticky top-8">
                            <div className="mb-6">
                                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                                    <Layers className="w-6 h-6" style={{ color: myriadeColors.blue }} />
                                    Tous nos services
                                </h3>
                                <p className="text-gray-400 text-sm">
                                    Découvrez notre gamme complète de solutions digitales
                                </p>
                            </div>

                            <div className="space-y-4">
                                {services.map((service, index) => {
                                    const Icon = service.icon;
                                    return (
                                        <motion.div
                                            key={index}
                                            whileHover={{ x: 10 }}
                                            onClick={() => setActiveService(index)}
                                            className={`group cursor-pointer p-4 rounded-xl border transition-all backdrop-blur-sm ${activeService === index
                                                ? 'shadow-lg'
                                                : 'hover:border-gray-600 hover:shadow-lg'
                                                }`}
                                            style={activeService === index ? {
                                                background: `linear-gradient(135deg, ${getServiceLightColor(service)}, rgba(31, 41, 55, 0.5))`,
                                                borderColor: `${service.colorValue}40`
                                            } : {
                                                background: 'rgba(31, 41, 55, 0.3)',
                                                borderColor: 'rgba(255, 255, 255, 0.1)'
                                            }}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                                                    style={activeService === index ? {
                                                        background: getServiceGradient(service)
                                                    } : {
                                                        background: getServiceLightColor(service)
                                                    }}>
                                                    <Icon className="w-6 h-6"
                                                        style={{ color: activeService === index ? 'white' : service.colorValue }} />
                                                </div>

                                                <div className="flex-1">
                                                    <h4 className={`font-medium ${activeService === index ? 'text-white' : 'text-gray-200'}`}>
                                                        {service.title}
                                                    </h4>
                                                    <p className="text-sm"
                                                        style={{ color: activeService === index ? service.colorValue : '#9ca3af' }}>
                                                        {service.tagline}
                                                    </p>
                                                </div>

                                                <ChevronRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${activeService === index ? 'text-white' : 'text-gray-400'}`} />
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ServicesDisplay;
